import React, { useMemo, useState } from "react";

import { Autocomplete, Box, Button, Paper, SxProps, TextField, Theme, Typography } from "@mui/material";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { useInvestorsSelector } from "store";

type InvestorOption = { id: number; name: string };
type SeparatorOption = { id: string; name: string; isSeparator: true };
type InvestorOptionType = InvestorOption | SeparatorOption;

interface Props {
  sx?: SxProps<Theme>;
  disabled?: boolean;
  loading?: boolean;
  dealInvestors: { id: number; name: string; is_splittable?: boolean }[];
  preselectedInvestorId: number | null;
  onChange: (value: { id: number; name: string } | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  allInvestorsId?: number;
  showGrouped?: boolean;
}

export const InvestorAutocomplete: React.FC<Props> = ({
  disabled,
  loading,
  dealInvestors,
  preselectedInvestorId,
  onChange,
  label,
  sx,
  error,
  helperText,
  allInvestorsId,
  showGrouped = true
}) => {
  const investors = useInvestorsSelector();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const investorsFromStore = useMemo(
    () => investors.map((investor) => ({ id: investor.id, name: investor.name })),
    [investors]
  );

  const investorsOptions = useMemo<InvestorOptionType[]>(() => {
    const accountInvestors = investorsFromStore.filter(
      (investor) => !dealInvestors.some((dealInvestor) => dealInvestor.id === investor.id)
    );

    const options: InvestorOptionType[] = [];
    const useCommitmentSections = allInvestorsId !== undefined;

    if (!showGrouped) {
      // Flat list without separators
      options.push(...dealInvestors, ...accountInvestors);
      return options;
    }

    if (useCommitmentSections) {
      const allInvestorsEntry = dealInvestors.find((d) => d.id === allInvestorsId);
      const remainingDealInvestors = dealInvestors.filter((d) => d.id !== allInvestorsId);

      if (allInvestorsEntry && allInvestorsEntry.is_splittable) {
        options.push({ id: "separator-commitment", name: "SPLIT BY COMMITMENT", isSeparator: true });
        options.push(allInvestorsEntry);
      }
      if (remainingDealInvestors.length) {
        options.push({ id: "separator-deal", name: "DEAL INVESTORS", isSeparator: true });
        options.push(...remainingDealInvestors);
      }
      if (accountInvestors.length) {
        options.push({ id: "separator-account", name: "OTHER INVESTORS", isSeparator: true });
        options.push(...accountInvestors);
      }
    } else {
      if (dealInvestors.length) {
        options.push({ id: "separator-deal", name: "DEAL INVESTORS", isSeparator: true });
        options.push(...dealInvestors);
      }
      if (accountInvestors.length) {
        options.push({ id: "separator-account", name: "ACCOUNT INVESTORS", isSeparator: true });
        options.push(...accountInvestors);
      }
    }

    return options;
  }, [investorsFromStore, dealInvestors, allInvestorsId, showGrouped]);

  const handleInvestorSelect = ({ investor_id }: { investor_id: number }) => {
    onChange({ id: investor_id, name: investors.find((investor) => investor.id === investor_id)?.name || "" });
    setIsDrawerOpen(false);
  };

  return (
    <>
      <Autocomplete
        disabled={disabled}
        loading={loading}
        sx={{ width: "100%", ...sx }}
        options={investorsOptions}
        value={investorsOptions.find((opt) => !("isSeparator" in opt) && opt.id === preselectedInvestorId) || null}
        getOptionLabel={(option) => {
          if ("isSeparator" in option) return "";
          return option.name;
        }}
        onChange={(_, value) => {
          if (value && !("isSeparator" in value)) {
            onChange({ id: value.id, name: value.name });
          } else {
            onChange(null);
          }
        }}
        slots={{
          paper: (props) => (
            <Paper
              {...props}
              sx={[
                ...(props.sx ? (Array.isArray(props.sx) ? props.sx : [props.sx]) : []),
                {
                  position: "relative",
                  backgroundColor: "white",
                  zIndex: 1500,
                  borderRadius: "10px",
                  "& .MuiAutocomplete-listbox": {
                    maxHeight: "28vh",
                    "& .MuiAutocomplete-option[aria-disabled='true']": {
                      opacity: 1,
                      "&.Mui-focused": {
                        opacity: 1
                      }
                    },
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                      bgcolor: "#edf1f9",
                      "&.Mui-focused": {
                        bgcolor: "#edf1f9"
                      }
                    }
                  },
                  "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                    bgcolor: "#edf1f9"
                  }
                }
              ]}
            >
              {props.children}
              <Button
                onMouseDown={() => {
                  setIsDrawerOpen(true);
                }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  height: "50px",
                  backgroundColor: "#fff",
                  boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                  borderRadius: "0 0 10px 10px"
                }}
                fullWidth
              >
                Add Investor
              </Button>
            </Paper>
          )
        }}
        slotProps={{
          popper: {
            placement: "bottom-start",
            modifiers: [
              {
                name: "flip",
                enabled: true,
                options: {
                  fallbackPlacements: ["top-start"],
                  boundary: "viewport",
                  padding: 8
                }
              },
              { name: "preventOverflow", enabled: true, options: { rootBoundary: "viewport", padding: 8 } }
            ]
          }
        }}
        renderOption={(props, option) => {
          if ("isSeparator" in option) {
            return (
              <Box
                key={option.name}
                display="flex"
                alignItems="center"
                padding="6px 20px"
                sx={{ pointerEvents: "none", backgroundColor: "#F6F8FC" }}
              >
                <Typography fontSize="12px" fontWeight="bold" sx={{ color: "#496682" }}>
                  {option.name}
                </Typography>
              </Box>
            );
          }
          return (
            <li
              style={{
                minHeight: "43px",
                fontSize: "16px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                display: "flex",
                alignItems: "center"
              }}
              {...props}
              key={option.id}
            >
              {option.name}
            </li>
          );
        }}
        isOptionEqualToValue={(option, value) => {
          if ("isSeparator" in option || (value && "isSeparator" in value)) return false;
          if (!value || "isSeparator" in value) return false;
          return option.id === value.id;
        }}
        getOptionDisabled={(option) => "isSeparator" in option}
        renderInput={(params) => {
          return (
            <TextField
              placeholder="Select"
              {...params}
              label={label}
              error={error}
              helperText={helperText}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          );
        }}
      />
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response) => {
          handleInvestorSelect({ investor_id: response.id });
        }}
      />
    </>
  );
};
